import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Select, Typography, Skeleton, Empty, Tooltip, Button, Space, Divider } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * GenerationFormatPicker (Dropdown version)
 *
 * Purpose:
 *  - Lets user select a publication/generation format via a single dropdown.
 *  - Keeps API payload stable: emits a numeric `formatId` via onChange.
 *  - Uses Ant Design components for visual parity with the new UI.
 *
 * Props:
 *  - value: number | string | null            -> currently selected format id
 *  - onChange: (formatId: number) => void     -> selection callback (emits number)
 *  - data: Array<{ id: number|string, name?: Record<string,string>|string, description?: string, preview?: string }>
 *      - If `data` is not provided, the component can call `load` (below) to fetch.
 *  - loading: boolean                         -> external loading flag (optional)
 *  - error: string | null                     -> external error message (optional)
 *  - load: () => Promise<void>                -> optional loader to refetch formats (useful if you keep API in the page)
 *  - i18nLang: string                         -> language code (e.g. 'en', 'ru', 'kk') to pick localized name if object
 *
 * Behavior:
 *  - Normalizes id types (string/number) to ensure equality and API safety.
 *  - Renders Skeleton while loading; Empty + optional Reload on no data/error.
 *  - Emits only when selection changes; no unnecessary re-renders (useMemo/useCallback).
 *  - Accessible: keyboard search, clearable (optional), and tooltips for previews.
 */

const { Text } = Typography;

const normalizeId = (id) => {
  // Convert to number when possible; fall back to original for equality checks.
  if (id === null || id === undefined) return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

const getLocalizedName = (name, lang) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  // name can be an object like { en: "...", ru: "...", kk: "..." }
  // Fallback order: lang -> en -> first available key
  return name[lang] || name.en || name[Object.keys(name)[0]] || "";
};

export default function GenerationFormatPicker({
  value,
  onChange,
  data,
  loading = false,
  error = null,
  load,
  i18nLang = "en",
}) {
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = loading || internalLoading;
  const {t} = useTranslation();

  // Normalize the incoming list once; avoid per-item work on each render.
  const items = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((it) => ({
      ...it,
      __id: normalizeId(it.id),
      __name: getLocalizedName(it.name, i18nLang),
      __desc: it.description || "",
      __preview: it.preview || "",
    }));
  }, [data, i18nLang]);

  // Selected ID normalized (ensures strict comparison works).
  const selectedId = useMemo(() => normalizeId(value), [value]);

  const handleSelect = useCallback(
    (id) => {
      const normalized = normalizeId(id);
      // Emit only if changed (prevents unnecessary parent updates).
      if (String(normalized) !== String(selectedId)) {
        // Coerce to number if possible for API stability.
        const n = Number(normalized);
        onChange?.(Number.isFinite(n) ? n : normalized);
      }
    },
    [onChange, selectedId]
  );

  // Optional self-loading when no data provided but a loader exists.
  useEffect(() => {
    if (!data && typeof load === "function") {
      setInternalLoading(true);
      Promise.resolve()
        .then(() => load())
        .catch(() => {
          /* swallow, parent should set error */
        })
        .finally(() => setInternalLoading(false));
    }
  }, [data, load]);

  // Build <Select> options once; each option packs id + text + tooltip preview
  const options = useMemo(
    () =>
      items.map((fmt) => ({
        label: (
          <Space direction="vertical" size={2}>
            <Text ellipsis title={fmt.__name}>
              {fmt.__name || "Untitled format"}
            </Text>
            {fmt.__desc ? (
              <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {fmt.__desc}
              </Text>
            ) : null}
          </Space>
        ),
        value: fmt.__id,
        // Use `title` for built-in tooltip of the option label + explicit Tooltip for preview
        title: fmt.__name,
        __preview: fmt.__preview,
      })),
    [items]
  );

  // Custom dropdown footer: show preview hint + optional reload
  const dropdownRender = useCallback(
    (menu) => (
      <>
        {menu}
        <Divider style={{ margin: "8px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", padding: "0 8px 8px" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {error ? error : "Choose a publication format"}
          </Text>
          {typeof load === "function" && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => {
                // Trigger a reload while keeping UX responsive
                setInternalLoading(true);
                Promise.resolve()
                  .then(() => load())
                  .finally(() => setInternalLoading(false));
              }}
            >
              Reload
            </Button>
          )}
        </div>
      </>
    ),
    [error, load]
  );

  // When loading: keep layout stable with a disabled Select + Skeleton
  if (isLoading) {
    return (
      <div>
        <Select
          style={{ width: "100%" }}
          loading
          disabled
          placeholder="Loading formats..."
          options={[]}
        />
        <div style={{ marginTop: 8 }}>
          <Skeleton active paragraph={{ rows: 1 }} title={false} />
        </div>
      </div>
    );
  }

  // Error / Empty state: disabled Select with helpful message and inline Reload
  if (error || options.length === 0) {
    return (
      <div>
        <Select
          style={{ width: "100%" }}
          disabled
          placeholder={error || "No formats available"}
          options={[]}
          notFoundContent={<Empty description={error || "No formats"} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          dropdownRender={dropdownRender}
        />
      </div>
    );
  }

  return (
    <Select
      style={{ width: "100%" }}
      // Show the current selection; normalized for strict equality
      value={selectedId ?? undefined}
      onChange={handleSelect}
      placeholder={t("home.formatPost")}
      options={options}
      // Allow searching by visible text (name + description)
      showSearch
      filterOption={(input, option) => {
        if (!option) return false;
        const labelText = typeof option.label === "string"
          ? option.label
          : // Extract text from our label node (name + maybe desc)
            (option.label?.props?.children?.[0]?.props?.children || "") +
            " " +
            (option.label?.props?.children?.[1]?.props?.children || "");
        return String(labelText).toLowerCase().includes(input.toLowerCase());
      }}
      // Add a small footer with Reload/help; also shows error text if any
      dropdownRender={dropdownRender}
      // Render a compact single-line in the trigger; rely on option.label inside dropdown
      optionLabelProp="title"
      // For portals: keep popup inside parent if needed (helps with modals/drawers)
      getPopupContainer={(trigger) => trigger.parentNode}
      // Optional: clearable UX (only if you want to allow "no selection")
      allowClear
      // Tooltip preview on hover of each option’s eye icon, if provided
      optionRender={(option) => {
        const hasPreview = option.data.__preview;
        return (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            {option.label}
            {hasPreview ? (
              <Tooltip title={option.data.__preview}>
                <EyeOutlined />
              </Tooltip>
            ) : null}
          </Space>
        );
      }}
    />
  );
}
